<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { Filters } from "~/core/models/filters";
import { PATH_CAMPAIGNS_UID } from "~/modules/campaign/constants";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import { useDtwinOperationModels } from "~/modules/dtwin/composables/useDtwinOperationManager";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();

const router = useRouter();
const acl = useAcl();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  fetchData: fetchDtwinOperationModels,
  results: dtwinOperationModelsResults,
  setFilter: setDtwinOperationModelsFilter,
} = useDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

const canManageCampaign = computed(() => {
  return (
    acl.can(ACLRoles.CAN_READ_DEVICE) &&
    dtwinOperationModelsResults.value?.results &&
    dtwinOperationModelsResults.value?.results?.length > 0
  );
});

const showCampaignOperationMgrFunnel: Ref<boolean> = ref(false);
function showNewCampaignFunnel() {
  showCampaignOperationMgrFunnel.value = true;
}

onMounted(async () => {
  setDtwinOperationModelsFilter(Filters.DTWIN_OPERATION_MODEL_HIDDEN, "false");
  const params = new Map();
  params.set(CAMPAIGN_PARAMS_SCOPE, true);
  await fetchDtwinOperationModels(undefined, undefined, params);
});
</script>

<template>
  <page-component check-license>
    <template #top-right>
      <lxc-button
        v-if="canManageCampaign"
        :icon="ILxcPlus"
        class="h-11"
        size="large"
        type="primary"
        :title="t('campaign.newCampaign')"
        @click="showNewCampaignFunnel"
      >
        {{ t("campaign.newCampaign") }}
      </lxc-button>
      <campaign-operation-mgr-funnel
        v-model:show="showCampaignOperationMgrFunnel"
        @saved="
          (uid) => router.push({ name: PATH_CAMPAIGNS_UID, params: { uid } })
        "
      />
    </template>
    <campaign-operation-mgr-list />
  </page-component>
</template>
